<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import debounce from 'lodash/debounce'
import startCase from 'lodash/startCase'

const props = defineProps({
    baseLocale: {
        type: String,
        required: true,
    },
    assignedLocales: {
        type: Array,
        default: () => [],
    },
})

const STORAGE_KEY = 'mf-translations-workspace'
const DEFAULT_STATUSES = ['missing', 'stale', 'needs_review']

const locales = ref([])
const selectedLocale = ref('')
const localeError = ref('')
const loadingLocales = ref(true)

const categories = ref([])
const categorySummary = ref(null)
const loadingCategories = ref(false)
const categoriesError = ref('')

const entries = ref([])
const loadingEntries = ref(false)
const entriesError = ref('')

const filters = reactive({
    category: '',
    search: '',
    statuses: [...DEFAULT_STATUSES],
    sort: 'key',
    direction: 'asc',
    page: 1,
    perPage: 50,
})

const pagination = reactive({
    current_page: 1,
    last_page: 1,
    total: 0,
    from: 0,
    to: 0,
})

const statusOptions = [
    { value: 'missing', label: 'Missing' },
    { value: 'stale', label: 'Out of sync' },
    { value: 'needs_review', label: 'Needs review' },
    { value: 'draft', label: 'Draft' },
    { value: 'translated', label: 'Translated' },
]

const sortOptions = [
    { value: 'key', label: 'Key' },
    { value: 'category', label: 'Category' },
    { value: 'updated_at', label: 'Updated' },
    { value: 'state', label: 'Status' },
]

const perPageOptions = [25, 50, 100]

const storedSettings = ref(null)

const summaryTotals = computed(() => {
    if (!categorySummary.value) {
        return {
            total: 0,
            missing: 0,
            stale: 0,
            needs_review: 0,
            draft: 0,
            translated: 0,
        }
    }

    return {
        total: categorySummary.value.total ?? 0,
        missing: categorySummary.value.missing ?? 0,
        stale: categorySummary.value.stale ?? 0,
        needs_review: categorySummary.value.needs_review ?? 0,
        draft: categorySummary.value.draft ?? 0,
        translated: categorySummary.value.translated ?? 0,
    }
})

const statusCounts = computed(() => summaryTotals.value)

const activeStatusesDescriptor = computed(() => {
    const active = filters.statuses
    if (!active.length) {
        return 'Showing all statuses'
    }

    return `Showing ${active.length} status${active.length > 1 ? 'es' : ''}`
})

const formatEntry = (item) => {
    const translation = item.translation || {}
    const value = translation.value ?? ''
    const state = translation.state ?? 'draft'

    return {
        ...item,
        metadata: item.metadata || {},
        translation: {
            ...translation,
            value,
            state,
            editValue: value,
            originalValue: value,
            originalState: state,
            saving: false,
            error: null,
        },
    }
}

const persistSettings = () => {
    if (!selectedLocale.value) {
        return
    }

    const payload = {
        locale: selectedLocale.value,
        category: filters.category,
        statuses: filters.statuses,
        sort: filters.sort,
        direction: filters.direction,
        perPage: filters.perPage,
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
}

const loadStoredSettings = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (raw) {
            return JSON.parse(raw)
        }
    } catch (error) {
        console.warn('Failed to parse stored translation settings', error)
    }

    return null
}

const applyStoredSettings = () => {
    const stored = storedSettings.value
    if (!stored) {
        return
    }

    if (Array.isArray(stored.statuses) && stored.statuses.length) {
        filters.statuses = [...new Set(stored.statuses)]
    }

    if (stored.category) {
        filters.category = stored.category
    }

    if (stored.sort) {
        filters.sort = stored.sort
    }

    if (stored.direction) {
        filters.direction = stored.direction
    }

    if (stored.perPage) {
        filters.perPage = stored.perPage
    }
}

const loadLocales = async () => {
    loadingLocales.value = true
    localeError.value = ''

    try {
        const response = await axios.get(route('api.translations.locales'))
        const available = response.data.locales || []
        const assigned = available.filter((locale) => locale.assigned)

        locales.value = assigned

        let preferred = storedSettings.value?.locale
        if (preferred && !assigned.find((locale) => locale.code === preferred)) {
            preferred = null
        }

        if (!preferred && props.assignedLocales.length) {
            preferred = props.assignedLocales.find((locale) => assigned.find((item) => item.code === locale)) || null
        }

        if (!preferred && assigned.length) {
            preferred = assigned[0].code
        }

        selectedLocale.value = preferred || ''

        if (!selectedLocale.value) {
            localeError.value = 'You do not have any locales assigned yet. Please contact an administrator.'
        } else {
            applyStoredSettings()
        }
    } catch (error) {
        console.error(error)
        localeError.value = 'Failed to load locales. Please refresh the page.'
    } finally {
        loadingLocales.value = false
    }
}

let categoriesRequestId = 0
const loadCategories = async () => {
    if (!selectedLocale.value) {
        return
    }

    categoriesError.value = ''
    loadingCategories.value = true
    const requestId = ++categoriesRequestId

    try {
        const response = await axios.get(route('api.translations.categories'), {
            params: {
                locale: selectedLocale.value,
            },
        })

        if (requestId !== categoriesRequestId) {
            return
        }

        categories.value = response.data.data || []
        categorySummary.value = response.data.summary || null
    } catch (error) {
        console.error(error)
        if (requestId === categoriesRequestId) {
            categoriesError.value = 'Unable to load category breakdown right now.'
        }
    } finally {
        if (requestId === categoriesRequestId) {
            loadingCategories.value = false
        }
    }
}

let entriesRequestId = 0
const loadEntries = async () => {
    if (!selectedLocale.value) {
        return
    }

    entriesError.value = ''
    const requestId = ++entriesRequestId
    loadingEntries.value = true

    try {
        const params = {
            locale: selectedLocale.value,
            page: filters.page,
            per_page: filters.perPage,
            sort: filters.sort,
            direction: filters.direction,
        }

        if (filters.category) {
            params.category = filters.category
        }

        if (filters.search) {
            params.search = filters.search
        }

        if (filters.statuses.length) {
            params.statuses = filters.statuses
        }

        const response = await axios.get(route('api.translations.entries'), { params })

        if (requestId !== entriesRequestId) {
            return
        }

        const payload = response.data
        entries.value = (payload.data || []).map(formatEntry)

        if (payload.meta) {
            if (filters.page !== payload.meta.current_page) {
                filters.page = payload.meta.current_page
            }
            pagination.current_page = payload.meta.current_page
            pagination.last_page = payload.meta.last_page
            pagination.total = payload.meta.total
            pagination.from = payload.meta.from
            pagination.to = payload.meta.to
        } else {
            pagination.current_page = filters.page
            pagination.last_page = 1
            pagination.total = entries.value.length
            pagination.from = entries.value.length ? 1 : 0
            pagination.to = entries.value.length
        }
    } catch (error) {
        console.error(error)
        if (requestId === entriesRequestId) {
            entriesError.value = error.response?.data?.message || 'Unable to load translations.'
        }
    } finally {
        if (requestId === entriesRequestId) {
            loadingEntries.value = false
        }
    }
}

const refreshAll = async () => {
    persistSettings()
    await Promise.all([loadCategories(), loadEntries()])
}

const searchDebounced = debounce(() => {
    filters.page = 1
    loadEntries()
}, 300)

const toggleStatus = (value) => {
    const index = filters.statuses.indexOf(value)
    if (index > -1) {
        filters.statuses.splice(index, 1)
    } else {
        filters.statuses.push(value)
    }
    persistSettings()
}

const isStatusActive = (value) => filters.statuses.includes(value)

const resetStatuses = () => {
    filters.statuses = [...DEFAULT_STATUSES]
    persistSettings()
}

const selectCategory = (category) => {
    if (filters.category === category) {
        filters.category = ''
    } else {
        filters.category = category
    }
    persistSettings()
}

const changeDirection = () => {
    filters.direction = filters.direction === 'asc' ? 'desc' : 'asc'
    persistSettings()
}

const goToPage = (page) => {
    if (page < 1 || page > pagination.last_page || page === filters.page) {
        return
    }

    filters.page = page
}

const entryIsDirty = (entry) => {
    return (
        entry.translation.editValue !== entry.translation.originalValue ||
        entry.translation.state !== entry.translation.originalState
    )
}

const resetEntry = (entry) => {
    entry.translation.editValue = entry.translation.originalValue
    entry.translation.state = entry.translation.originalState
    entry.translation.error = null
}

const updateEntryLocally = (entry, payload) => {
    entry.metadata = payload.metadata || {}
    entry.source_value = payload.source_value
    entry.source_updated_at = payload.source_updated_at
    entry.source_last_imported_at = payload.source_last_imported_at
    entry.is_html = payload.is_html
    entry.is_plural = payload.is_plural
    entry.status = payload.status

    const translated = payload.translation || {}
    entry.translation = {
        ...entry.translation,
        ...translated,
        value: translated.value ?? '',
        state: translated.state ?? 'draft',
        editValue: translated.value ?? '',
        originalValue: translated.value ?? '',
        originalState: translated.state ?? 'draft',
        saving: false,
        error: null,
    }
}

const saveEntry = async (entry, { markReviewed = false } = {}) => {
    entry.translation.saving = true
    entry.translation.error = null

    try {
        const response = await axios.put(
            route('api.translations.entries.update', {
                translationKey: entry.id,
                locale: selectedLocale.value,
            }),
            {
                value: entry.translation.editValue,
                state: entry.translation.state,
                mark_reviewed: markReviewed,
            }
        )

        const updatedPayload = response.data?.data || response.data
        if (updatedPayload) {
            updateEntryLocally(entry, updatedPayload)
        }

        await loadCategories()
    } catch (error) {
        console.error(error)
        entry.translation.error = error.response?.data?.message || 'Failed to save translation.'
    } finally {
        entry.translation.saving = false
    }
}

const markAsReviewed = (entry) => {
    entry.translation.state = 'translated'
    saveEntry(entry, { markReviewed: true })
}

const markNeedsReview = (entry) => {
    entry.translation.state = 'needs_review'
    saveEntry(entry)
}

const placeholderList = (entry) => {
    const placeholders = entry.metadata?.placeholders
    if (!Array.isArray(placeholders)) {
        return []
    }
    return placeholders
}

const pluralForms = (entry) => {
    const forms = entry.metadata?.plural_forms
    if (!Array.isArray(forms)) {
        return []
    }
    return forms
}

const formatCategoryLabel = (category) => {
    if (!category) {
        return 'All strings'
    }

    return startCase(category.replace(/[-_]/g, ' '))
}

watch(
    () => filters.search,
    () => {
        filters.page = 1
        searchDebounced()
    }
)

watch(
    () => [filters.category, filters.sort, filters.direction, filters.perPage],
    () => {
        filters.page = 1
        persistSettings()
        loadEntries()
    }
)

watch(
    () => filters.statuses.slice().sort().join(','),
    () => {
        filters.page = 1
        persistSettings()
        loadEntries()
    }
)

watch(
    () => filters.page,
    (page, previous) => {
        if (page === previous) {
            return
        }
        loadEntries()
    }
)

watch(selectedLocale, async (locale, previous) => {
    if (locale === previous) {
        return
    }

    filters.page = 1
    persistSettings()
    await refreshAll()
})

onMounted(async () => {
    storedSettings.value = loadStoredSettings()
    await loadLocales()
})
</script>

<template>
    <div>
        <div class="container w-full mx-auto top-2 z-topbar md:sticky">
            <frosted-bar>
                <div class="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <h1 class="text-xl font-semibold">Translation Workspace</h1>
                        <p class="text-xs text-gray-400">
                            Manage JavaScript translation strings imported from the repository.
                        </p>
                    </div>
                    <div class="flex flex-wrap items-center gap-3">
                        <div>
                            <label class="block text-xs font-semibold text-gray-500">Locale</label>
                            <select
                                v-model="selectedLocale"
                                class="border rounded-md px-3 py-1 text-sm focus:outline-none focus:ring focus:border-primary"
                                :disabled="loadingLocales || !locales.length"
                            >
                                <option v-for="locale in locales" :key="locale.code" :value="locale.code">
                                    {{ locale.label }}
                                </option>
                            </select>
                        </div>
                        <div>
                            <label class="block text-xs font-semibold text-gray-500">Search</label>
                            <input
                                v-model="filters.search"
                                type="search"
                                placeholder="Key, source, or translation"
                                class="border rounded-md px-3 py-1 text-sm w-48 focus:outline-none focus:ring focus:border-primary"
                                :disabled="!selectedLocale"
                            />
                        </div>
                        <button-secondary
                            size="sm"
                            @click="refreshAll"
                            :disabled="!selectedLocale || loadingEntries"
                        >
                            Refresh
                        </button-secondary>
                    </div>
                </div>
            </frosted-bar>
        </div>

        <div class="container mx-auto px-4 md:px-8 py-6 space-y-6">
            <div v-if="loadingLocales" class="py-8 flex justify-center">
                <mf-spinner-medium class="w-24 h-12"></mf-spinner-medium>
            </div>

            <div v-else>
                <div
                    v-if="localeError"
                    class="p-4 border border-amber-200 bg-amber-50 rounded text-sm text-amber-700"
                >
                    {{ localeError }}
                </div>

                <div v-else class="grid gap-6 lg:grid-cols-12">
                    <aside class="space-y-6 lg:col-span-4 xl:col-span-3">
                        <section class="p-4 rounded-wl bg-white shadow-xl">
                            <div class="flex items-center justify-between">
                                <h2 class="text-sm font-semibold">Status filters</h2>
                                <button-transparent
                                    size="line"
                                    class="text-xs"
                                    @click="resetStatuses"
                                >
                                    Reset
                                </button-transparent>
                            </div>
                            <p class="text-xs text-gray-400 mb-3">
                                {{ activeStatusesDescriptor }}
                            </p>
                            <div class="flex flex-wrap gap-2">
                                <button
                                    v-for="status in statusOptions"
                                    :key="status.value"
                                    class="px-3 py-1 rounded-full text-xs border transition"
                                    :class="isStatusActive(status.value)
                                        ? 'border-primary bg-primary/10 text-primary'
                                        : 'border-gray-200 text-gray-500 hover:border-primary/40'"
                                    @click="toggleStatus(status.value)"
                                >
                                    <span class="font-medium">{{ status.label }}</span>
                                    <span class="ml-1 text-gray-400">{{ statusCounts[status.value] ?? 0 }}</span>
                                </button>
                            </div>
                        </section>

                        <section class="p-4 rounded-wl bg-white shadow-xl">
                            <div class="flex items-center justify-between mb-3">
                                <h2 class="text-sm font-semibold">Categories</h2>
                                <span class="text-xs text-gray-400">{{ categories.length }} groups</span>
                            </div>

                            <div v-if="loadingCategories" class="py-6 flex justify-center">
                                <mf-spinner-small></mf-spinner-small>
                            </div>

                            <div v-else>
                                <button
                                    class="w-full text-left px-3 py-2 rounded-md text-sm flex justify-between items-center transition"
                                    :class="!filters.category ? 'bg-primary/10 text-primary border border-primary/40' : 'hover:bg-gray-50 border border-transparent'"
                                    @click="selectCategory('')"
                                >
                                    <span class="font-medium">All strings</span>
                                    <span class="text-xs text-gray-500">{{ summaryTotals.total }}</span>
                                </button>

                                <div class="mt-3 space-y-2 max-h-96 overflow-y-auto pr-1">
                                    <button
                                        v-for="category in categories"
                                        :key="category.category"
                                        class="w-full text-left px-3 py-2 rounded-md text-sm flex justify-between items-center transition"
                                        :class="filters.category === category.category ? 'bg-primary/10 text-primary border border-primary/40' : 'hover:bg-gray-50 border border-transparent'"
                                        @click="selectCategory(category.category)"
                                    >
                                        <span class="truncate">
                                            {{ formatCategoryLabel(category.category) }}
                                        </span>
                                        <div class="flex items-center gap-2 text-xs">
                                            <span class="text-gray-500">{{ category.total }}</span>
                                            <span class="text-red-500" v-if="category.missing">• {{ category.missing }}</span>
                                            <span class="text-amber-500" v-else-if="category.stale">• {{ category.stale }}</span>
                                        </div>
                                    </button>
                                </div>

                                <p v-if="categoriesError" class="mt-3 text-xs text-red-500">
                                    {{ categoriesError }}
                                </p>
                            </div>
                        </section>
                    </aside>

                    <main class="lg:col-span-8 xl:col-span-9 space-y-4">
                        <section class="flex flex-wrap items-center justify-between gap-3">
                            <div class="flex items-center gap-2">
                                <label class="text-xs font-semibold text-gray-500">Sort by</label>
                                <select
                                    v-model="filters.sort"
                                    class="border rounded-md px-3 py-1 text-sm focus:outline-none focus:ring focus:border-primary"
                                >
                                    <option v-for="option in sortOptions" :key="option.value" :value="option.value">
                                        {{ option.label }}
                                    </option>
                                </select>
                                <button-transparent size="line" @click="changeDirection">
                                    <span class="text-xs uppercase tracking-wide">
                                        {{ filters.direction === 'asc' ? 'Asc' : 'Desc' }}
                                    </span>
                                </button-transparent>
                            </div>

                            <div class="flex items-center gap-2">
                                <label class="text-xs font-semibold text-gray-500">Per page</label>
                                <select
                                    v-model.number="filters.perPage"
                                    class="border rounded-md px-3 py-1 text-sm focus:outline-none focus:ring focus:border-primary"
                                >
                                    <option v-for="option in perPageOptions" :key="option" :value="option">
                                        {{ option }}
                                    </option>
                                </select>
                            </div>
                        </section>

                        <section>
                            <div v-if="loadingEntries" class="py-12 flex justify-center">
                                <mf-spinner-medium class="w-24 h-12"></mf-spinner-medium>
                            </div>

                            <div
                                v-else-if="entriesError"
                                class="p-4 border border-red-200 bg-red-50 rounded text-sm text-red-700"
                            >
                                {{ entriesError }}
                            </div>

                            <div v-else>
                                <div v-if="!entries.length" class="p-8 border border-dashed border-gray-300 rounded text-sm text-gray-500 text-center">
                                    No translations match your filters yet.
                                </div>

                                <list-basic v-else class="space-y-3">
                                    <list-basic-item
                                        v-for="entry in entries"
                                        :key="entry.id"
                                    >
                                        <template #title>
                                            <div class="flex flex-wrap justify-between gap-2 items-start">
                                                <div>
                                                    <p class="text-xs font-mono text-gray-500">
                                                        {{ entry.category }} • {{ entry.key }}
                                                    </p>
                                                    <div class="flex flex-wrap gap-2 mt-1">
                                                        <span
                                                            class="px-2 py-0.5 rounded-full text-xs font-medium"
                                                            :class="{
                                                                'bg-red-100 text-red-700': entry.status === 'missing',
                                                                'bg-amber-100 text-amber-700': entry.status === 'stale',
                                                                'bg-yellow-100 text-yellow-700': entry.status === 'needs_review',
                                                                'bg-gray-100 text-gray-600': entry.status === 'draft',
                                                                'bg-emerald-100 text-emerald-700': entry.status === 'translated',
                                                            }"
                                                        >
                                                            {{ statusOptions.find((option) => option.value === entry.status)?.label || 'Unknown' }}
                                                        </span>
                                                        <span v-if="entry.is_html" class="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700 font-medium">
                                                            HTML
                                                        </span>
                                                        <span v-if="entry.is_plural" class="px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700 font-medium">
                                                            Plural
                                                        </span>
                                                    </div>
                                                </div>

                                                <div class="text-xs text-right text-gray-400 space-y-1">
                                                    <div v-if="entry.source_updated_at">
                                                        Source updated {{ new Date(entry.source_updated_at).toLocaleString() }}
                                                    </div>
                                                    <div v-if="entry.translation.updated_at">
                                                        Translated {{ new Date(entry.translation.updated_at).toLocaleString() }}
                                                    </div>
                                                    <div v-if="entry.translation.updated_by" class="text-gray-500">
                                                        By {{ entry.translation.updated_by.name }}
                                                    </div>
                                                </div>
                                            </div>
                                        </template>

                                        <template #default>
                                            <div class="grid gap-4 md:grid-cols-2">
                                                <div>
                                                    <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Source</h3>
                                                    <div class="text-sm whitespace-pre-wrap text-gray-700 border border-gray-100 bg-gray-50 p-3 rounded">
                                                        {{ entry.source_value }}
                                                    </div>

                                                    <div v-if="placeholderList(entry).length" class="mt-3">
                                                        <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Placeholders</h4>
                                                        <div class="flex flex-wrap gap-2">
                                                            <span
                                                                v-for="placeholder in placeholderList(entry)"
                                                                :key="placeholder"
                                                                class="px-2 py-0.5 bg-gray-100 rounded font-mono text-xs text-gray-600"
                                                            >
                                                                {{ placeholder }}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div v-if="pluralForms(entry).length" class="mt-3">
                                                        <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Plural forms</h4>
                                                        <div class="flex flex-wrap gap-2">
                                                            <span
                                                                v-for="form in pluralForms(entry)"
                                                                :key="form"
                                                                class="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs font-medium"
                                                            >
                                                                {{ form }}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div class="space-y-3">
                                                    <div>
                                                        <div class="flex items-center justify-between mb-1">
                                                            <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Translation</h3>
                                                            <select
                                                                v-model="entry.translation.state"
                                                                class="border rounded px-2 py-1 text-xs focus:outline-none focus:ring focus:border-primary"
                                                            >
                                                                <option value="translated">Translated</option>
                                                                <option value="needs_review">Needs review</option>
                                                                <option value="draft">Draft</option>
                                                            </select>
                                                        </div>
                                                        <textarea
                                                            v-model="entry.translation.editValue"
                                                            class="w-full border rounded-md p-3 text-sm focus:outline-none focus:ring focus:border-primary"
                                                            rows="4"
                                                        ></textarea>
                                                    </div>

                                                    <div class="flex flex-wrap items-center gap-2">
                                                        <button-primary
                                                            size="sm"
                                                            :disabled="entry.translation.saving || !entryIsDirty(entry)"
                                                            @click="saveEntry(entry)"
                                                        >
                                                            Save
                                                        </button-primary>
                                                        <button-secondary
                                                            size="sm"
                                                            :disabled="entry.translation.saving || !entryIsDirty(entry)"
                                                            @click="resetEntry(entry)"
                                                        >
                                                            Reset
                                                        </button-secondary>
                                                        <button-transparent
                                                            size="sm"
                                                            :disabled="entry.translation.saving"
                                                            @click="markAsReviewed(entry)"
                                                        >
                                                            Mark reviewed
                                                        </button-transparent>
                                                        <button-transparent
                                                            size="sm"
                                                            :disabled="entry.translation.saving"
                                                            class="text-amber-600"
                                                            @click="markNeedsReview(entry)"
                                                        >
                                                            Needs attention
                                                        </button-transparent>
                                                    </div>

                                                    <p v-if="entry.translation.error" class="text-xs text-red-600">
                                                        {{ entry.translation.error }}
                                                    </p>
                                                </div>
                                            </div>
                                        </template>
                                    </list-basic-item>
                                </list-basic>
                            </div>
                        </section>

                        <section v-if="pagination.last_page > 1" class="flex flex-wrap items-center justify-between gap-3">
                            <div class="text-xs text-gray-500">
                                Showing
                                <span class="font-medium">{{ pagination.from }}</span>
                                –
                                <span class="font-medium">{{ pagination.to }}</span>
                                of
                                <span class="font-medium">{{ pagination.total }}</span>
                                strings
                            </div>

                            <div class="flex items-center gap-2">
                                <button-secondary size="sm" :disabled="filters.page <= 1" @click="goToPage(filters.page - 1)">
                                    Previous
                                </button-secondary>
                                <span class="text-xs text-gray-500">
                                    Page {{ filters.page }} of {{ pagination.last_page }}
                                </span>
                                <button-secondary size="sm" :disabled="filters.page >= pagination.last_page" @click="goToPage(filters.page + 1)">
                                    Next
                                </button-secondary>
                            </div>
                        </section>
                    </main>
                </div>
            </div>
        </div>
    </div>
</template>
